// The backend splits `additional_options` on ";" and on the first "=", and each
// entry reaches the bot as `{bot_name}_KEY`. Editing one key must leave the rest
// untouched: a job configured by hand can carry keys this build never declares.

export interface OptionToken {
  key: string;
  value: string;
}

export function parseOptions(raw: string | null | undefined): OptionToken[] {
  return (raw || "")
    .split(";")
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const eq = part.indexOf("=");
      if (eq === -1) return { key: part, value: "" };
      return {
        key: part.slice(0, eq).trim(),
        value: part.slice(eq + 1).trim(),
      };
    });
}

export function serializeOptions(tokens: OptionToken[]): string {
  return tokens
    .filter((t) => t.key)
    .map((t) => (t.value === "" ? t.key : `${t.key}=${t.value}`))
    .join(";");
}

/**
 * Only the entries whose key is one of `keys` (case-insensitive), order kept.
 * For a form whose bot changed: the previous bot's options mean nothing to the
 * next one, and kept they would be saved with the job unseen.
 */
export function keepOptions(
  raw: string | null | undefined,
  keys: Iterable<string>,
): string {
  const keep = new Set(Array.from(keys, (key) => key.toUpperCase()));
  return serializeOptions(
    parseOptions(raw).filter((t) => keep.has(t.key.toUpperCase())),
  );
}

export function readOption(
  raw: string | null | undefined,
  key: string,
): string | null {
  const found = parseOptions(raw).find(
    (t) => t.key.toLowerCase() === key.toLowerCase(),
  );
  return found ? found.value : null;
}

/** Set a key, or drop it entirely when `value` is null. Order is preserved. */
export function writeOption(
  raw: string | null | undefined,
  key: string,
  value: string | null,
): string {
  const tokens = parseOptions(raw);
  const idx = tokens.findIndex(
    (t) => t.key.toLowerCase() === key.toLowerCase(),
  );
  if (value === null) {
    if (idx !== -1) tokens.splice(idx, 1);
  } else if (idx === -1) {
    tokens.push({ key, value });
  } else {
    tokens[idx] = { key: tokens[idx].key, value };
  }
  return serializeOptions(tokens);
}

// Every reader splits the string on ";" before it decodes a value, so a ";"
// inside a list or map is written as its JSON escape: JSON.parse and json.loads
// both turn it back into ";".
const SEMICOLON_ESCAPE = "\\u003b";

/** A list or map as the bot entrypoint (parse_additional_options) reads it. */
export function encodeOptionJson(value: unknown): string {
  return JSON.stringify(value).replace(/;/g, SEMICOLON_ESCAPE);
}

// Strict JSON first: an apostrophe inside a question or an answer is not a
// quote. The single-quoted Python-literal spelling of older hand-written rows
// is the fallback.
function parseOptionJson(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    try {
      return JSON.parse(value.replace(/'/g, '"'));
    } catch {
      return undefined;
    }
  }
}

export function readListOption(
  raw: string | null | undefined,
  key: string,
): string[] {
  const value = readOption(raw, key);
  if (!value) return [];
  const parsed = parseOptionJson(value);
  return Array.isArray(parsed) ? parsed.map(String) : [];
}

export function writeListOption(
  raw: string | null | undefined,
  key: string,
  values: string[],
): string {
  const clean = values.map((v) => v.trim()).filter(Boolean);
  return writeOption(raw, key, clean.length ? encodeOptionJson(clean) : null);
}

export function readMapOption(
  raw: string | null | undefined,
  key: string,
): Record<string, string> {
  const value = readOption(raw, key);
  if (!value) return {};
  const parsed = parseOptionJson(value);
  return parsed && typeof parsed === "object" && !Array.isArray(parsed)
    ? (parsed as Record<string, string>)
    : {};
}

export function writeMapOption(
  raw: string | null | undefined,
  key: string,
  map: Record<string, string>,
): string {
  const clean = Object.fromEntries(
    Object.entries(map)
      .map(([k, v]) => [k.trim(), (v || "").trim()])
      .filter(([k, v]) => k && v),
  );
  return writeOption(
    raw,
    key,
    Object.keys(clean).length ? encodeOptionJson(clean) : null,
  );
}

// The one key with a checkbox of its own, because it applies to every bot.
export const OPTION_KEYS = {
  skipErrorEmployees: "SKIP_ERROR_EMPLOYEES",
} as const;

export interface SkipErrorEmployeesParse {
  processErrors: boolean;
  remaining: string;
}

/** Inverted on purpose: the bot skips error employees unless told otherwise. */
export function extractSkipErrorEmployees(
  raw: string | null | undefined,
): SkipErrorEmployeesParse {
  const value = readOption(raw, OPTION_KEYS.skipErrorEmployees);
  return {
    processErrors: (value || "").toLowerCase() === "false",
    remaining: writeOption(raw, OPTION_KEYS.skipErrorEmployees, null),
  };
}

export function applySkipErrorEmployees(
  remaining: string,
  processErrors: boolean,
): string {
  return writeOption(
    remaining,
    OPTION_KEYS.skipErrorEmployees,
    processErrors ? "False" : null,
  );
}
